/**
 * Staff Profile Configuration Page
 *
 * View + edit a staff member's profile fields (mirrors the member custom-field
 * system for staff/users). Fields are grouped into the 7 Staff Profile sections
 * and backed by /api/staff-profiles. Setting "Platform Access Level" syncs the
 * staff member's system role server-side.
 */

'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { UserPlus } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { useRole } from '@/contexts/RoleContext';
import { PageHeader, PageContainer } from '@/components/weavein';
import { evaluateVisibilityOperator } from '@/components/forms/fieldVisibility';
import { isStaffBound, useOrgStaffOptions } from '@/components/forms/staffOptions';
import { useCapability } from '@/hooks/useCapabilities';
import { openStaffTrainingCompliance } from '@/src/app/workforce/training-program/deepLink';
import PersonTrainingPanel from '@/src/app/workforce/training-program/PersonTrainingPanel';
import { AddStaffDialog } from './AddStaffDialog';

interface FieldOption {
  label: string;
  value: string;
}
interface VisibilityCondition {
  field: string;
  operator: string;
  value?: any;
}
interface VisibilityRules {
  logic?: 'AND' | 'OR';
  conditions?: VisibilityCondition[];
  when_hidden?: string;
}
interface StaffField {
  id: number;
  name: string;
  friendly_name: string;
  type: string;
  category: string;
  field_options?: FieldOption[] | null;
  /** Non-null means the options are loaded at render time, not stored on the
   *  field. 'org_staff' = the org's own staff list (migration 686). */
  options_source?: string | null;
  mandatory: boolean;
  visibility_rules?: VisibilityRules | null;
  value: any;
}
interface FieldGroup {
  category: string;
  label: string;
  fields: StaffField[];
}
interface StaffUser {
  id: number;
  name: string;
  email: string;
  phone?: string | null;
  role?: string | null;
}
interface TrainingRollup {
  open: number;
  overdue: number;
  due_soon: number;
  pending: number;
  completed: number;
  unevaluated: number;
}
interface RosterEntry {
  user_id: number;
  name: string;
  email: string;
  phone?: string | null;
  role?: string | null;
  has_profile: boolean;
  /** Present only in the roster view — keyed by com_fields name. */
  values?: Record<string, any>;
  training?: TrainingRollup;
}
/** A column the ORG configured, not one this page hardcoded. */
interface RosterColumn {
  name: string;
  friendly_name: string;
  type: string;
  category: string;
  category_label: string;
  field_options?: FieldOption[] | null;
  options_source?: string | null;
  /** Whether THIS org made the field mandatory. Drives which fields the Add
   *  Staff form asks for, so it is the org's config and not a hardcoded list. */
  mandatory?: boolean;
}

/** The categories worth putting in a table by default. The rest stay on the profile. */
const TABLE_CATEGORIES = ['identity', 'staff_role', 'role_employment', 'reporting_line', 'programs'];
/** Columns wide enough to bury the ones beside them. */
const MAX_DEFAULT_COLUMNS = 6;

function displayValue(v: any): string {
  if (v === null || v === undefined || v === '') return '';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

function authHeaders(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const cboId = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
}

/**
 * The options a field actually offers.
 *
 * A field whose `options_source` is 'org_staff' carries NO stored options —
 * its list is the org's current staff, fetched at render time, which is the
 * point: a new hire shows up in the dropdown without anyone editing the field.
 */
function optionsForField(
  field: { name: string; field_options?: FieldOption[] | null; options_source?: string | null },
  staffOptions: FieldOption[],
): FieldOption[] {
  if (isStaffBound(field.name, field.options_source)) return staffOptions;
  return normalizeOptions(field.field_options);
}

/**
 * What to SHOW for a stored value.
 *
 * Staff-bound fields store a user id, so the raw value is a number nobody
 * recognises — the roster table, the CSV export and the filter dropdowns all
 * have to resolve it back to a name.
 *
 * A value with no matching option is returned as-is rather than blanked. That
 * is not a tidy-up: Amigos' CEO has "Board of Directors" recorded as her
 * manager, which is true and is not a staff member, and an unresolved id is
 * better shown than silently swallowed.
 */
function labelForValue(
  field: { name: string; field_options?: FieldOption[] | null; options_source?: string | null },
  raw: any,
  staffOptions: FieldOption[],
): string {
  const shown = displayValue(raw);
  if (!shown) return '';
  const match = optionsForField(field, staffOptions).find((o) => String(o.value) === shown);
  return match ? match.label : shown;
}

function normalizeOptions(opts?: FieldOption[] | null): FieldOption[] {
  if (!Array.isArray(opts)) return [];
  return opts.map((o: any) => (typeof o === 'string' ? { label: o, value: o } : o));
}

export default function StaffProfilesPage() {
  // The roster table is the staff-training program's admin surface, so it follows that
  // capability. An org without it keeps the picker it has always had — unchanged, not
  // a degraded version of something new.
  const orgId = useMemo(() => {
    const raw = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;
    const parsed = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, []);
  const { enabled: rosterView } = useCapability(orgId, 'staff_training');

  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [columns, setColumns] = useState<RosterColumn[]>([]);
  const [search, setSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<number | null>(null);
  const [user, setUser] = useState<StaffUser | null>(null);
  const [groups, setGroups] = useState<FieldGroup[]>([]);
  const [values, setValues] = useState<Record<string, any>>({});
  // The values as last loaded or saved, so the page can say whether anything is
  // unsaved (TRUS-562: "there isn't a clear Save, so it's not clear if a change has
  // been made").
  const [savedValues, setSavedValues] = useState<Record<string, any>>({});
  const [justSaved, setJustSaved] = useState(false);
  // Org admins ARE included here, unlike the frontline pickers this hook was
  // built for: at Amigos the two people with the most direct reports are
  // org_admin, and a manager list without them is wrong.
  const staffOptions = useOrgStaffOptions({ includeOrgAdmins: true });
  const { currentRole } = useRole();
  // Mirrors the server's _require_staff_admin (super_admin / org_admin /
  // ops_admin) rather than the subTabRegistry ADMIN_ROLES, which is a DIFFERENT
  // list for a different purpose — it carries program_manager and not
  // ops_admin. Using that one would show the button to a program manager and
  // hand them a 403 when they pressed it.
  const canAddStaff = ['super_admin', 'org_admin', 'ops_admin'].includes(
    String(currentRole || '').toLowerCase(),
  );
  // Who may edit profiles. Mirrors the server's STAFF_PROFILE_ADMIN_ROLES (TRUS-592):
  // everyone else who reaches this page is a manager looking at their own staff, and
  // the server rejects their saves, so they get a read-only view instead (TRUS-700).
  const canEditProfiles = ['super_admin', 'org_admin', 'ops_admin', 'program_manager'].includes(
    String(currentRole || '').toLowerCase(),
  );
  // "all", or "reporting_line" when the server returned only the caller's own staff.
  const [rosterScope, setRosterScope] = useState<'all' | 'reporting_line'>('all');
  const [addOpen, setAddOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadRoster = useCallback(async () => {
    try {
      // The extras are opt-in on the server too, so the picker still costs one query.
      const url = rosterView
        ? '/api/staff-profiles/roster?include_fields=true&include_training=true'
        : '/api/staff-profiles/roster';
      const res = await fetch(getWeaveinApiUrl(url), {
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`roster ${res.status}`);
      const data = await res.json();
      setRoster(data.staff || []);
      setColumns(data.columns || []);
      setRosterScope(data.scope === 'reporting_line' ? 'reporting_line' : 'all');
    } catch (e: any) {
      setError(`Couldn't load staff roster: ${e.message}`);
    }
  }, [rosterView]);

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  const loadProfile = useCallback(async (userId: number) => {
    setLoading(true);
    setMessage(null);
    setError(null);
    try {
      const res = await fetch(getWeaveinApiUrl(`/api/staff-profiles/${userId}`), {
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`profile ${res.status}`);
      const data = await res.json();
      setUser(data.user || null);
      setGroups(data.groups || []);
      const initial: Record<string, any> = {};
      (data.groups || []).forEach((g: FieldGroup) =>
        g.fields.forEach((f) => {
          initial[f.name] = f.value ?? (f.type === 'multi_select' || f.type === 'checkbox' ? [] : '');
        })
      );
      setValues(initial);
      setSavedValues(initial);
      setJustSaved(false);
    } catch (e: any) {
      setError(`Couldn't load profile: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const isDirty = useMemo(() => JSON.stringify(values) !== JSON.stringify(savedValues), [values, savedValues]);

  // Leaving a profile with unsaved edits asks first, rather than dropping them silently.
  const confirmDiscard = () =>
    !isDirty || window.confirm('You have unsaved changes to this profile. Leave without saving?');

  const selectStaff = (userId: number) => {
    if (selected != null && userId !== selected && !confirmDiscard()) return;
    setSelected(userId);
    loadProfile(userId);
  };

  const setValue = (name: string, v: any) => {
    setJustSaved(false);
    setValues((prev) => ({ ...prev, [name]: v }));
  };

  const toggleMulti = (name: string, optValue: string) => {
    setJustSaved(false);
    setValues((prev) => {
      const cur: string[] = Array.isArray(prev[name]) ? prev[name] : [];
      return { ...prev, [name]: cur.includes(optValue) ? cur.filter((x) => x !== optValue) : [...cur, optValue] };
    });
  };

  const save = async () => {
    if (selected == null) return;
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const res = await fetch(getWeaveinApiUrl(`/api/staff-profiles/${selected}`), {
        method: 'PUT',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({ values }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail || `save ${res.status}`);
      }
      const data = await res.json();
      setSavedValues(values);
      setJustSaved(true);
      setMessage(
        `Saved ${data.saved} field${data.saved === 1 ? '' : 's'}.` +
          (data.role_synced ? ` Platform role synced to "${data.role_synced}".` : '')
      );
      loadRoster();
    } catch (e: any) {
      setError(`Couldn't save: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // TRUS-535: honor a staff field's conditional visibility_rules, the same way
  // the member profile, household form and form renderers do. Conditions
  // reference the parent field by its com_fields `name`, and `values` is keyed
  // by field name, so a direct lookup resolves the dependency. Operator
  // semantics come from the shared helper so a rule configured once behaves
  // identically on every surface. when_hidden is 'optional' on these fields —
  // the stored value is kept, just not shown, so flipping a terminated employee
  // back to Active does not silently erase their termination date.
  const isFieldVisible = (f: StaffField): boolean => {
    const rules = f.visibility_rules;
    if (!rules || !rules.conditions || rules.conditions.length === 0) return true;
    const results = rules.conditions.map((cond) =>
      evaluateVisibilityOperator(cond.operator, values[cond.field], cond.value)
    );
    return rules.logic === 'OR' ? results.some(Boolean) : results.every(Boolean);
  };

  // Which configured fields become table columns. Driven by the org's own field
  // categories, so an org that groups its staff fields differently gets a different
  // table without anybody editing this file.
  const tableColumns = useMemo(() => {
    const preferred = columns.filter((c) => TABLE_CATEGORIES.includes(c.category));
    return (preferred.length > 0 ? preferred : columns).slice(0, MAX_DEFAULT_COLUMNS);
  }, [columns]);

  // Only single-choice fields make sensible filter dropdowns; free text would produce
  // one option per person.
  const filterableColumns = useMemo(
    () => tableColumns.filter((c) => c.type === 'select' && optionsForField(c, staffOptions).length > 0),
    [tableColumns, staffOptions]
  );

  const visibleRoster = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return roster.filter((r) => {
      if (needle) {
        const hay = [
          r.name,
          r.email,
          // Resolved through the column defs, not raw: a staff-bound field
          // stores a user id, and searching ids is not something anyone does.
          ...columns.map((c) => labelForValue(c, r.values?.[c.name], staffOptions)),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      for (const [name, wanted] of Object.entries(columnFilters)) {
        if (!wanted) continue;
        if (name === '__training__') {
          const t = r.training;
          if (!t) return false;
          if (wanted === 'overdue' && t.overdue === 0) return false;
          if (wanted === 'due_soon' && t.due_soon === 0) return false;
          if (wanted === 'none' && t.open + t.completed > 0) return false;
        } else if (displayValue(r.values?.[name]) !== wanted) {
          return false;
        }
      }
      return true;
    });
    // columns + staffOptions are read inside, so the search has to recompute
    // once the staff list arrives — otherwise typing a manager's name before
    // the fetch lands matches nothing and never recovers.
  }, [roster, search, columnFilters, columns, staffOptions]);

  const exportCsv = () => {
    const header = ['Name', 'Email', 'Phone', 'Platform role', ...tableColumns.map((c) => c.friendly_name)];
    if (rosterView) header.push('Overdue trainings', 'Due soon', 'Completed');
    const escape = (cell: string) => (/[",\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell);
    const lines = [header.map(escape).join(',')];
    for (const r of visibleRoster) {
      const row = [
        r.name || '',
        r.email || '',
        r.phone || '',
        r.role || '',
        ...tableColumns.map((c) => labelForValue(c, r.values?.[c.name], staffOptions)),
      ];
      if (rosterView) {
        row.push(String(r.training?.overdue ?? 0), String(r.training?.due_soon ?? 0), String(r.training?.completed ?? 0));
      }
      lines.push(row.map(escape).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `staff-roster-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderField = (f: StaffField) => {
    const v = values[f.name];
    const base = 'w-full border border-v2border rounded-md px-3 py-2 text-sm';
    if (f.type === 'select') {
      const opts = optionsForField(f, staffOptions);
      const cur = displayValue(v);
      // A stored value the option list does not contain stays selectable, or
      // opening the record would show "—" and the next save would write that
      // emptiness back over a real answer. Amigos' CEO has "Board of
      // Directors" as her manager; a staff picker will never offer it.
      const unmatched = cur && !opts.some((o) => String(o.value) === cur);
      return (
        <select className={base} value={v ?? ''} onChange={(e) => setValue(f.name, e.target.value)}>
          <option value="">—</option>
          {unmatched && <option value={cur}>{cur}</option>}
          {opts.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }
    if (f.type === 'multi_select' || f.type === 'checkbox') {
      const cur: string[] = Array.isArray(v) ? v : [];
      return (
        <div className="flex flex-wrap gap-2">
          {optionsForField(f, staffOptions).map((o) => {
            const on = cur.includes(o.value);
            return (
              <button
                type="button"
                key={o.value}
                onClick={() => toggleMulti(f.name, o.value)}
                className={`px-2.5 py-1 rounded-full text-xs border ${
                  on ? 'bg-blue-600 text-white border-blue-600' : 'bg-surface text-ink-700 border-v2border'
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      );
    }
    if (f.type === 'date') {
      return <input type="date" className={base} value={v ?? ''} onChange={(e) => setValue(f.name, e.target.value)} />;
    }
    if (f.type === 'int') {
      return (
        <input
          type="number"
          min={0}
          className={base}
          value={v ?? ''}
          onChange={(e) => setValue(f.name, e.target.value)}
        />
      );
    }
    return <input type="text" className={base} value={v ?? ''} onChange={(e) => setValue(f.name, e.target.value)} />;
  };

  // One Save control, shown at the top of a profile and again at the bottom, with a
  // status beside it so an edit never looks saved when it isn't.
  const saveControls = (
    <div className="flex flex-wrap items-center gap-3">
      <button
        type="button"
        onClick={save}
        disabled={saving || !isDirty}
        className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {saving ? 'Saving…' : 'Save changes'}
      </button>
      {saving ? null : isDirty ? (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Unsaved changes</span>
      ) : justSaved ? (
        <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">All changes saved</span>
      ) : null}
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        // TRUS-700: "Staff" for orgs running the staff module (Amigos). Every other org
        // keeps the page exactly as it was until the standard Staff section lands.
        eyebrow={rosterView ? 'Workforce Management' : 'Admin · Configuration'}
        title={rosterView ? 'Staff' : 'Staff Profiles'}
        description={
          !rosterView ? (
            <>
              View and edit staff profile fields. Setting <strong>Platform Access Level</strong> updates the staff
              member&apos;s platform role.
            </>
          ) : canEditProfiles ? (
            <>
              The people who do your organization&apos;s work. Setting <strong>Platform Access Level</strong> updates
              the staff member&apos;s platform role.
            </>
          ) : (
            <>Your staff: everyone who reports to you, directly or through another manager. View only.</>
          )
        }
        actions={
          canAddStaff ? (
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-[13px] font-medium text-white hover:opacity-90"
            >
              <UserPlus className="h-4 w-4" /> Add staff member
            </button>
          ) : undefined
        }
      />
      <PageContainer>

      {error && <div className="mt-4 rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}
      {message && <div className="mt-4 rounded-md bg-green-50 text-green-700 px-3 py-2 text-sm">{message}</div>}

      <div className={rosterView ? 'mt-6' : 'mt-6 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6'}>
        {/* Roster table. Capability-gated: an org without the staff-training program
            keeps the picker below, unchanged. */}
        {rosterView && selected == null && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email or any profile value"
                className="min-w-[240px] flex-1 border border-v2border rounded-md px-3 py-2 text-sm"
              />
              {filterableColumns.map((c) => (
                <select
                  key={c.name}
                  className="border border-v2border rounded-md px-2 py-2 text-sm"
                  value={columnFilters[c.name] || ''}
                  onChange={(e) => setColumnFilters((prev) => ({ ...prev, [c.name]: e.target.value }))}
                >
                  <option value="">All {c.friendly_name.toLowerCase()}</option>
                  {optionsForField(c, staffOptions).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ))}
              <select
                className="border border-v2border rounded-md px-2 py-2 text-sm"
                value={columnFilters.__training__ || ''}
                onChange={(e) => setColumnFilters((prev) => ({ ...prev, __training__: e.target.value }))}
              >
                <option value="">Any training status</option>
                <option value="overdue">Has overdue</option>
                <option value="due_soon">Has due soon</option>
                <option value="none">Nothing assigned</option>
              </select>
              <button
                onClick={exportCsv}
                className="border border-v2border rounded-md px-3 py-2 text-sm text-ink-700 hover:bg-paper"
              >
                Export CSV
              </button>
            </div>

            <div className="text-xs text-ink-500">
              Showing {visibleRoster.length} of {roster.length} staff
              {rosterScope === 'reporting_line' ? ' who report to you' : ''}
            </div>
            {rosterScope === 'reporting_line' && roster.length === 0 && (
              <div className="rounded-lg border border-dashed border-v2border p-6 text-sm text-ink-500">
                Nobody reports to you yet. Reporting lines come from the Manager field on each staff profile; an
                administrator can set it.
              </div>
            )}

            <div className="border border-v2border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-paper text-left text-xs uppercase tracking-wide text-ink-500">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Name</th>
                    {tableColumns.map((c) => (
                      <th key={c.name} className="px-3 py-2 font-semibold whitespace-nowrap">
                        {c.friendly_name}
                      </th>
                    ))}
                    <th className="px-3 py-2 font-semibold whitespace-nowrap">Training</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRoster.map((r) => (
                    <tr key={r.user_id} className="border-t border-v2border-muted hover:bg-paper">
                      <td className="px-3 py-2">
                        <button onClick={() => selectStaff(r.user_id)} className="text-left">
                          <div className="font-medium text-ink">{r.name || `User ${r.user_id}`}</div>
                          <div className="text-xs text-ink-500">{r.email}</div>
                        </button>
                        {/* TRUS-766: the actions sit under the name. In a last column they
                            were cut off on narrower screens, so nobody found them. */}
                        <div className="mt-1 flex gap-3 whitespace-nowrap">
                          <button
                            onClick={() => selectStaff(r.user_id)}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Profile
                          </button>
                          <button
                            onClick={() => openStaffTrainingCompliance(r.user_id)}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Trainings
                          </button>
                        </div>
                      </td>
                      {tableColumns.map((c) => (
                        <td key={c.name} className="px-3 py-2 text-ink-700">
                          {labelForValue(c, r.values?.[c.name], staffOptions) || '\u2014'}
                        </td>
                      ))}
                      <td className="px-3 py-2 whitespace-nowrap">
                        {r.training && (r.training.open > 0 || r.training.completed > 0) ? (
                          <div className="flex flex-wrap gap-1">
                            {r.training.overdue > 0 && (
                              <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] text-red-700">
                                {r.training.overdue} overdue
                              </span>
                            )}
                            {r.training.due_soon > 0 && (
                              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] text-amber-800">
                                {r.training.due_soon} due soon
                              </span>
                            )}
                            {r.training.completed > 0 && (
                              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] text-emerald-700">
                                {r.training.completed} done
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-ink-400">none assigned</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {visibleRoster.length === 0 && (
                    <tr>
                      <td
                        colSpan={tableColumns.length + 2}
                        className="px-3 py-6 text-center text-sm text-ink-400"
                      >
                        {roster.length === 0 ? 'No staff found.' : 'No staff match those filters.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Roster picker, unchanged for orgs without the staff-training program. */}
        {!rosterView && (
        <div className="border border-v2border rounded-lg overflow-hidden self-start">
          <div className="px-3 py-2 bg-paper text-xs font-semibold uppercase tracking-wide text-ink-500">
            Staff ({roster.length})
          </div>
          <div className="max-h-[560px] overflow-y-auto">
            {roster.map((r) => (
              <button
                key={r.user_id}
                onClick={() => selectStaff(r.user_id)}
                className={`w-full text-left px-3 py-2 border-t border-v2border-muted hover:bg-paper ${
                  selected === r.user_id ? 'bg-blue-50' : ''
                }`}
              >
                <div className="text-sm font-medium text-ink">{r.name || `User ${r.user_id}`}</div>
                <div className="text-xs text-ink-500">{r.email}</div>
                <div className="text-[11px] text-ink-400 mt-0.5">
                  {r.role || 'no role'}
                  {r.has_profile ? ' · profile started' : ''}
                </div>
              </button>
            ))}
            {roster.length === 0 && <div className="px-3 py-4 text-sm text-ink-400">No staff found.</div>}
          </div>
        </div>
        )}

        {/* Editor */}
        {(!rosterView || selected != null) && (
        <div>
          {selected == null ? (
            <div className="text-sm text-ink-400 border border-dashed border-v2border rounded-lg p-8 text-center">
              Select a staff member to view and edit their profile.
            </div>
          ) : loading ? (
            <div className="text-sm text-ink-400 p-8">Loading profile…</div>
          ) : (
            <div>
              {user && (
                <div className="mb-4 border border-v2border rounded-lg p-4">
                  {rosterView && (
                    <button
                      onClick={() => {
                        if (confirmDiscard()) setSelected(null);
                      }}
                      className="mb-2 text-xs text-blue-600 hover:underline"
                    >
                      &larr; Back to roster
                    </button>
                  )}
                  <div className="text-lg font-medium text-ink">{user.name}</div>
                  <div className="text-sm text-ink-500">
                    {user.email}
                    {user.phone ? ` · ${user.phone}` : ''} · role: {user.role || '—'}
                  </div>
                  {canEditProfiles && <div className="mt-3">{saveControls}</div>}
                </div>
              )}
              {/* TRUS-562: a manager (or admin) opening an employee's record sees their
                  whole training picture here — every assignment and its status, plus
                  completion records. Only where the staff training program is on. Placed
                  right under the header, not after every field section (TRUS-766). */}
              {rosterView && selected != null && (
                <div className="mb-5 border border-v2border rounded-lg overflow-hidden">
                  <div className="px-4 py-2 bg-paper text-sm font-semibold text-ink-700">Training</div>
                  <div className="p-4">
                    <PersonTrainingPanel userId={selected} />
                  </div>
                </div>
              )}
              {groups.map((g) => {
                const visible = g.fields.filter(isFieldVisible);
                if (visible.length === 0) return null;
                return (
                  <div key={g.category} className="mb-5 border border-v2border rounded-lg overflow-hidden">
                    <div className="px-4 py-2 bg-paper text-sm font-semibold text-ink-700">{g.label}</div>
                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {visible.map((f) => (
                        <div key={f.id}>
                          <label className="block text-xs font-medium text-ink-500 mb-1">
                            {f.friendly_name}
                            {f.mandatory && <span className="text-red-500"> *</span>}
                          </label>
                          {canEditProfiles ? (
                            renderField(f)
                          ) : (
                            <div className="text-sm text-ink min-h-[1.5rem]">
                              {labelForValue(f, f.name in values ? values[f.name] : f.value, staffOptions) || '\u2014'}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {canEditProfiles && (
              <div className="sticky bottom-0 bg-surface py-3 border-t border-v2border-muted">{saveControls}</div>
              )}
            </div>
          )}
        </div>
        )}
      </div>
      </PageContainer>

      <AddStaffDialog
        open={addOpen}
        columns={columns}
        staffOptions={staffOptions}
        onClose={() => setAddOpen(false)}
        onCreated={(userId, createdName) => {
          setAddOpen(false);
          // Says plainly what has and has NOT happened. The account exists and
          // can be assigned work; the person has been told nothing, and the
          // invite is a separate deliberate step.
          setMessage(
            `${createdName} was added. They have not been notified — send an invitation from User Management when you are ready.`,
          );
          void loadRoster();
          // Straight into their profile: the form asked only for what is
          // required, and the long tail of fields lives here.
          void loadProfile(userId);
        }}
      />
    </div>
  );
}
